/**
 * Client Messages Panel
 *
 * Panneau de messagerie global avec liste des conversations
 * Design System: Contexte DASHBOARD (text-gray-900, bg-white/70)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MessageCircle,
  Search,
  FolderKanban,
  Send,
  ChevronLeft,
  Wifi,
  WifiOff,
} from 'lucide-react';
import {
  useConversations,
  useProjectMessages,
  useSendMessage,
} from '../hooks/useClientPortal';
import { useClientPortalSSE } from '../hooks/useClientPortalSSE';
import type {
  ClientConversation,
  ClientMessage,
  SendMessageInput,
  NewMessageSSEEvent,
} from '../types/client-portal.types';

export function ClientMessagesPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: conversations, isLoading: conversationsLoading } = useConversations();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Real-time SSE connection (Sprint 3.3 - US-3.3.4)
  const handleNewMessage = useCallback((event: NewMessageSSEEvent) => {
    // Auto-scroll if we're viewing the same project
    if (event.projectId === selectedProjectId) {
      // TanStack Query cache is updated by the hook automatically
      // Just scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [selectedProjectId]);

  const { isConnected, typingUsers } = useClientPortalSSE({
    onNewMessage: handleNewMessage,
  });

  // Initialize selected project from URL
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId) {
      setSelectedProjectId(projectId);
    }
  }, [searchParams]);

  const { data: messages, isLoading: messagesLoading } = useProjectMessages(selectedProjectId || '');
  const sendMessage = useSendMessage();

  const [newMessage, setNewMessage] = useState('');
  // Note: messagesEndRef is used in SSE callback above
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get typing users for current project
  const currentTypingUsers = selectedProjectId ? typingUsers.get(selectedProjectId) || [] : [];

  // Filter conversations by search
  const filteredConversations = conversations?.filter((conv) =>
    conv.projectName.toLowerCase().includes(search.toLowerCase())
  );

  // Find selected conversation
  const selectedConversation = conversations?.find((c) => c.projectId === selectedProjectId);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSelectConversation = (projectId: string) => {
    setSelectedProjectId(projectId);
    setSearchParams({ project: projectId });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedProjectId) return;

    const input: SendMessageInput = {
      projectId: selectedProjectId,
      content: newMessage.trim(),
    };

    sendMessage.mutate(
      { projectId: selectedProjectId, input },
      {
        onSuccess: () => {
          setNewMessage('');
        },
      }
    );
  };

  const handleBack = () => {
    setSelectedProjectId(null);
    setSearchParams({});
  };

  if (conversationsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('common.clientPortal.messages.title')}</h1>
          <p className="text-gray-500 mt-1">
            {t('common.clientPortal.messages.subtitle')}
          </p>
        </div>
        {/* Connection Status Indicator */}
        <div className="flex items-center gap-2 text-sm">
          {isConnected ? (
            <>
              <Wifi className="w-4 h-4 text-green-500" />
              <span className="text-green-600">{t('common.clientPortal.messages.realtime')}</span>
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">{t('common.clientPortal.messages.connecting')}</span>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm overflow-hidden">
        <div className="flex h-[600px]">
          {/* Conversations List - Hidden on mobile when conversation selected */}
          <div
            className={`w-full md:w-80 lg:w-96 border-r border-gray-100 flex flex-col
              ${selectedProjectId ? 'hidden md:flex' : 'flex'}`}
          >
            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('common.clientPortal.messages.searchPlaceholder')}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl
                    text-gray-900 placeholder-gray-400
                    focus:ring-2 focus:ring-teal-500/50 focus:border-transparent
                    transition-all duration-200"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {(!filteredConversations || filteredConversations.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                    <MessageCircle className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500">
                    {search ? t('common.clientPortal.messages.noConversationFound') : t('common.clientPortal.messages.noConversation')}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredConversations.map((conv) => (
                    <ConversationItem
                      key={conv.projectId}
                      conversation={conv}
                      isSelected={conv.projectId === selectedProjectId}
                      onClick={() => handleSelectConversation(conv.projectId)}
                      formatDateTime={formatDateTime}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Messages Panel */}
          <div
            className={`flex-1 flex flex-col
              ${selectedProjectId ? 'flex' : 'hidden md:flex'}`}
          >
            {selectedProjectId && selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <button
                    onClick={handleBack}
                    className="md:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="p-2 bg-teal-100 rounded-xl">
                    <FolderKanban className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {selectedConversation.projectName}
                    </h3>
                  </div>
                  <button
                    onClick={() => navigate(`/portal/projects/${selectedProjectId}`)}
                    className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                  >
                    {t('common.clientPortal.messages.viewProject')}
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                    </div>
                  ) : !messages || messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3">
                        <MessageCircle className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500">{t('common.clientPortal.messages.noMessages')}</p>
                      <p className="text-sm text-gray-400">{t('common.clientPortal.messages.startConversation')}</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message) => (
                        <MessageBubble
                          key={message.id}
                          message={message}
                          formatDateTime={formatDateTime}
                        />
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Typing Indicator */}
                {currentTypingUsers.length > 0 && (
                  <div className="px-4 py-2 text-sm text-gray-500 italic animate-pulse">
                    {t('common.clientPortal.messages.typing', { users: currentTypingUsers.join(', ') })}
                  </div>
                )}

                {/* Input */}
                <div className="p-4 border-t border-gray-100">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      placeholder={t('common.clientPortal.messages.messagePlaceholder')}
                      disabled={sendMessage.isPending}
                      className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl
                        text-gray-900 placeholder-gray-400
                        focus:ring-2 focus:ring-teal-500/50 focus:border-transparent
                        transition-all duration-200 disabled:opacity-50"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendMessage.isPending}
                      className="px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl
                        shadow-lg hover:shadow-xl transition-all duration-300
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendMessage.isPending ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-6">
                <div>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <MessageCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('common.clientPortal.messages.selectConversation')}
                  </h3>
                  <p className="text-gray-500">
                    {t('common.clientPortal.messages.selectProjectToView')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Conversation Item Component
function ConversationItem({
  conversation,
  isSelected,
  onClick,
  formatDateTime,
}: {
  conversation: ClientConversation;
  isSelected: boolean;
  onClick: () => void;
  formatDateTime: (date: string) => string;
}) {
  const { t } = useTranslation();
  return (
    <div
      onClick={onClick}
      className={`p-4 cursor-pointer transition-colors ${
        isSelected ? 'bg-teal-50' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-xl ${isSelected ? 'bg-teal-100' : 'bg-gray-100'}`}>
          <FolderKanban className={`w-5 h-5 ${isSelected ? 'text-teal-600' : 'text-gray-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className={`font-semibold truncate ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>
              {conversation.projectName}
            </h3>
            {conversation.unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full flex-shrink-0">
                {conversation.unreadCount}
              </span>
            )}
          </div>
          {conversation.lastMessage && (
            <>
              <p className="text-sm text-gray-500 truncate mt-1">
                {conversation.lastMessage.isFromClient ? t('common.clientPortal.messages.you') : ''}
                {conversation.lastMessage.content}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {formatDateTime(conversation.lastMessage.createdAt)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Message Bubble Component
function MessageBubble({
  message,
  formatDateTime,
}: {
  message: ClientMessage;
  formatDateTime: (date: string) => string;
}) {
  return (
    <div className={`flex ${message.isFromClient ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-3 ${
          message.isFromClient
            ? 'bg-teal-600 text-white rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-bl-md'
        }`}
      >
        {!message.isFromClient && message.senderName && (
          <p className="text-xs font-semibold mb-1 text-teal-600">{message.senderName}</p>
        )}
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        {message.attachments && message.attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((attachment, index) => (
              <a
                key={index}
                href={attachment.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-xs underline ${
                  message.isFromClient ? 'text-white/80 hover:text-white' : 'text-teal-600 hover:text-teal-700'
                }`}
              >
                📎 {attachment.fileName}
              </a>
            ))}
          </div>
        )}
        <p
          className={`text-xs mt-1 ${message.isFromClient ? 'text-white/70' : 'text-gray-400'}`}
        >
          {formatDateTime(message.createdAt)}
        </p>
      </div>
    </div>
  );
}
